/**
 * @file Simple view router for bottom-tab navigation + nav stack
 */

const views = {};
let activeView = null;
let activeParams = null;
const navStack = [];

/**
 * Register a view with the router.
 * @param {string} name - View name (e.g. 'today', 'jobs')
 * @param {{ render: Function, mount: Function, unmount: Function }} view
 */
export function registerView(name, view) {
  views[name] = view;
}

/**
 * Navigate to a view (replaces current, no stack push for tab switches).
 * @param {string} viewName
 * @param {Object} [params]
 */
export function navigate(viewName, params) {
  if (!views[viewName]) {
    console.warn(`[router] Unknown view: ${viewName}`);
    return;
  }

  // Unmount current view
  if (activeView && views[activeView] && views[activeView].unmount) {
    try { views[activeView].unmount(); } catch (e) { console.warn('[router] unmount error:', e); }
  }

  // Clear nav stack on tab switch
  navStack.length = 0;

  activeView = viewName;
  activeParams = params || {};

  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = views[viewName].render(activeParams);
  }

  if (views[viewName].mount) {
    try { views[viewName].mount(activeParams); } catch (e) { console.warn('[router] mount error:', e); }
  }

  updateBottomNav(viewName);
}

/**
 * Push a sub-view onto the nav stack (e.g. job-detail from jobs list).
 * @param {string} viewName
 * @param {Object} [params]
 */
export function push(viewName, params) {
  if (!views[viewName]) {
    console.warn(`[router] Unknown view: ${viewName}`);
    return;
  }

  // Save current state to stack
  if (activeView) {
    navStack.push({ view: activeView, params: activeParams });
  }

  // Unmount current
  if (activeView && views[activeView] && views[activeView].unmount) {
    try { views[activeView].unmount(); } catch (e) { console.warn('[router] unmount error:', e); }
  }

  activeView = viewName;
  activeParams = params || {};

  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = views[viewName].render(activeParams);
  }

  if (views[viewName].mount) {
    try { views[viewName].mount(activeParams); } catch (e) { console.warn('[router] mount error:', e); }
  }
}

/**
 * Pop back to previous view in nav stack.
 */
export function pop() {
  if (navStack.length === 0) return;

  // Unmount current
  if (activeView && views[activeView] && views[activeView].unmount) {
    try { views[activeView].unmount(); } catch (e) { console.warn('[router] unmount error:', e); }
  }

  const prev = navStack.pop();
  activeView = prev.view;
  activeParams = prev.params;

  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = views[activeView].render(activeParams);
  }

  if (views[activeView].mount) {
    try { views[activeView].mount(activeParams); } catch (e) { console.warn('[router] mount error:', e); }
  }

  updateBottomNav(activeView);
}

/**
 * Update bottom nav active state.
 * @param {string} viewName
 */
export function updateBottomNav(viewName) {
  const tabMap = {
    'today': 'today',
    'jobs': 'jobs',
    'job-detail': 'jobs',
    'leads': 'leads',
    'lead-detail': 'leads',
    'tasks': 'tasks',
    'customers': 'customers',
    'customer-detail': 'customers',
    'timer': 'more',
    'camera': 'more',
    'more': 'more',
  };
  const activeTab = tabMap[viewName] || viewName;
  const items = document.querySelectorAll('.bottom-nav-item');
  items.forEach(item => {
    item.classList.toggle('active', item.dataset.tab === activeTab);
  });
}

/**
 * Get the currently active view name.
 * @returns {string|null}
 */
export function getActiveView() {
  return activeView;
}
